import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, makeStyles } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import { useHistory, useRouteMatch } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import useHelpContent from './useHelpContent'

const useStyles = makeStyles((theme) => ({
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dim-2)',
    padding: 'var(--dim-5)',
    color: 'var(--color-neutral-600)',
    fontSize: 'var(--font-size-sm)'
  },
  error: {
    padding: 'var(--dim-5)',
    color: 'var(--color-danger)',
    fontSize: 'var(--font-size-sm)'
  },
  markdown: {
    maxWidth: 800,
    lineHeight: 1.7,
    '& h1': {
      fontSize: '1.6rem',
      fontWeight: 600,
      margin: '0 0 var(--dim-4)',
      paddingBottom: 'var(--dim-2)',
      borderBottom: '1px solid var(--color-neutral-300)'
    },
    '& h2': {
      fontSize: '1.25rem',
      fontWeight: 600,
      margin: 'var(--dim-5) 0 var(--dim-2)'
    },
    '& h3': {
      fontSize: '1.05rem',
      fontWeight: 600,
      margin: 'var(--dim-4) 0 var(--dim-2)'
    },
    '& p': {
      margin: '0 0 var(--dim-3)',
      fontSize: 'var(--font-size-sm)'
    },
    '& ul, & ol': {
      margin: '0 0 var(--dim-3)',
      paddingLeft: 'var(--dim-5)',
      fontSize: 'var(--font-size-sm)'
    },
    '& li': {
      marginBottom: 'var(--dim-1)'
    },
    '& pre': {
      overflowX: 'auto',
      background: theme.palette.grey[100],
      padding: theme.spacing(2),
      borderRadius: 6,
      margin: '0 0 var(--dim-3)',
      fontSize: '0.85rem'
    },
    '& code': {
      background: theme.palette.grey[100],
      padding: '2px 6px',
      borderRadius: 3,
      fontSize: '0.85em'
    },
    '& pre code': {
      background: 'none',
      padding: 0
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '0 0 var(--dim-3)',
      fontSize: 'var(--font-size-sm)'
    },
    '& th': {
      textAlign: 'left',
      fontWeight: 600,
      padding: 'var(--dim-2) var(--dim-3)',
      borderBottom: '2px solid var(--color-neutral-300)',
      background: 'var(--color-neutral-50)'
    },
    '& td': {
      padding: 'var(--dim-2) var(--dim-3)',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& blockquote': {
      margin: '0 0 var(--dim-3)',
      padding: 'var(--dim-2) var(--dim-4)',
      borderLeft: '4px solid var(--color-primary)',
      background: 'var(--color-neutral-50)',
      '& p': {
        margin: 0
      }
    },
    '& hr': {
      border: 'none',
      borderTop: '1px solid var(--color-neutral-300)',
      margin: 'var(--dim-4) 0'
    },
    '& a': {
      color: '#1a6fbf',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      '&:hover': {
        color: '#124d85'
      },
      '&:visited': {
        color: '#6b4c9a'
      }
    }
  }
}))

const INTERNAL_MD_LINK = /\.md(#|$)/

function resolveInternalHref (href, currentSlug) {
  if (!href || !INTERNAL_MD_LINK.test(href)) return null
  try {
    const [pathPart, hash] = href.split('#')
    const base = `http://x/${currentSlug}`
    const resolved = new URL(pathPart, base)
    const slug = resolved.pathname.slice(1).replace(/\.md$/, '')
    return hash ? `${slug}#${hash}` : slug
  } catch {
    return null
  }
}

function HelpArticle ({ slug }) {
  const classes = useStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const { content, isLoading, error } = useHelpContent(slug)

  const helpBasePath = useMemo(() => {
    if (!slug) return '/help'
    const slugPath = `/${slug}`
    return url.endsWith(slugPath) ? url.slice(0, -slugPath.length) : '/help'
  }, [url, slug])

  const handleLinkClick = useCallback((e, href) => {
    const resolved = resolveInternalHref(href, slug)
    if (resolved) {
      e.preventDefault()
      history.push(`${helpBasePath}/${resolved}`)
    }
  }, [slug, history, helpBasePath])

  const markdownComponents = useMemo(() => ({
    a: ({ href, children, ...props }) => {
      const isInternal = href && INTERNAL_MD_LINK.test(href)
      if (isInternal) {
        return (
          <a
            {...props}
            href={href}
            onClick={(e) => handleLinkClick(e, href)}
          >
            {children}
          </a>
        )
      }
      const isSafeHref = href && /^https?:\/\/|^mailto:/i.test(href)
      return <a href={isSafeHref ? href : '#'} target='_blank' rel='noopener noreferrer' {...props}>{children}</a>
    }
  }), [handleLinkClick])

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading article...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.error}>
        This article could not be loaded. It may not exist yet.
      </div>
    )
  }

  if (!content) return null

  return (
    <div className={classes.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  )
}

HelpArticle.propTypes = {
  slug: PropTypes.string
}

export default HelpArticle
